/* Observables. Benugtung: 

Erzeugung:
const x = new Observable("initial Value")

Abhängige Werte:
const halfWindowWidth = windowWidth.map(x => x / 2)

Kombinierte Werte:
const dimensions = Observables.combine(
  [windowWidth, windowHeight], 
  ([width, height]) => 
    ({
      width, 
      height
    })
  )
)

undefined-Werte:
map und combine werden nur ausgeführt, wenn alle Eingabewerte != undefined sind.
Ist ein Eingabewert undefined, ist das Ergebnis undefined.

Optimierungen:
map, combine und watch werden nur ausgewertet, wenn sich der Wert gegenüber === verändert hat.
Observables von Objekten müssen also immer eine Kopie beim Setzen erstellen.

Beobachten: 
observable.watch - Jede Änderung wird sofort bearbeitet
observable.watchDebounced - Jede Änderung wird beim nächsten AnimationFrame bearbeitet
Ist der Wert != undefined, wird die Funktion bei der Registrierung sofort ausgeführt

Nicht mehr beobachten:
Der Rückgabewert von watch und watchDebounced ist eine Funktion, 
mit der die Beobachtung gestoppt werden kann

Debugging:
observable.log(name) - Jede Änderung wird auf der Konsole mit vorangestelltem Namen protokolliert
Der Rückgabewert ist this. Es geht also:
const x = new Observable().log('x')

*/

export default class Observable {
  constructor(value, filter = x => x) {
    this._value = value
    this._listener = []
    this._listenerDebounced = []
    this._waitingForAnimation = false
    this._filter = filter
    this._animation = $({_value: 0})

    this._handleAnimationFrame = () => {
      this._listenerDebounced.forEach(fn => fn(this._value))
      this._waitingForAnimation = false
    }
  }

  watch(fn) {
    this._listener.push(fn)
    if (typeof this.get() !== 'undefined') fn(this.get())
    return () => 
      this._listener = this._listener.filter(x => x !== fn)
  }

  watchDebounced(fn) {
    this._listenerDebounced.push(fn)
    if (typeof this.get() !== 'undefined') fn(this.get())
    return () => 
      this._listenerDebounced = this._listenerDebounced.filter(x => x !== fn)
  }

  set(value) {
    let nextValue = this._filter(value)
    if (this._value === nextValue) return
    const oldValue = this._value
    this._value = nextValue
    this._listener.forEach(fn => fn(nextValue, oldValue))
    if (this._listenerDebounced.length === 0) return;
    this._waitingForAnimation = true
    requestAnimationFrame(this._handleAnimationFrame)
  }

  animateTo(value, args = {}) {
    this._animation.stop()
    this._animation = $({_value: this._value}).animate({_value: value}, {
      ...args,
      step: this.set.bind(this)
    })
  }

  get() {
    return this._value
  }

  map(fn) {
    let result = new Observable((typeof this.get() !== 'undefined') ? fn(this.get()) : undefined)
    this.watch(value => result.set((typeof value !== 'undefined') ? fn(value) : undefined))
    return result
  }

  log(name) {
    console.log(name, this.get())
    this.watch(x => console.log(name, x))
    return this
  }
}

const getAll = observables =>
  observables.map(x => x.get())

Observable.combine = (observables = [], transformGet = x => x) => {
  const _getValue = () => {
    const values = getAll(observables)
    if (values.some(x => typeof x === 'undefined')) return undefined
    return transformGet(values)
  }

  let result = new Observable(_getValue())
  let _waitingForAnimation = false

  const _handleAnimationFrame = () => {
    result.set(_getValue())
    _waitingForAnimation = false
  }

  observables.forEach(observable =>
    observable.watch(() => {
      _waitingForAnimation = true
      _handleAnimationFrame()//requestAnimationFrame(_handleAnimationFrame)
    })
  )
  return result
}